import React from "react";
import { useNavigate } from "react-router-dom";
import { ConnectedFamilyCard } from "src/components/atoms/ConnectedFamilyCard/ConnectedFamilyCard";
import { useViewport } from "src/common/hooks/ViewPort";
import { showAlert } from "src/components/containers/Alert";
import { PageContainer } from "src/components/organisms/contents";
import { useFamilyRegister } from "src/common/hooks/userFamilyRegister";
import { request } from "src/common/api";
import { SubConHeader } from "src/components/atoms/SubHeaderBox/SubConHeader";
import { useEffect } from "react";
import { confirmDialog } from "src/components/containers/ConfirmDialog";
import { useUserRegister } from "src/common/hooks/useUserRegister";

declare global {
  interface Window {
    Kakao?: any;
    ReactNativeWebView?: { postMessage: (msg: string) => void };
    webkit?: {
      messageHandlers?: {
        kakaoShare?: { postMessage: (msg: string) => void };
      };
    };
  }
}

function InvitePage() {
  // family_oid 쿼리로 들어온 경우 유저 정보 업데이트 (초기 렌더 시)
  const familyOidHandledRef = React.useRef(false);
  const navi = useNavigate();
  const { px } = useViewport();
  const { data: userInfo } = useUserRegister();
  const { data: familyInfo } = useFamilyRegister();

  useEffect(() => {
    if (
      window.Kakao &&
      window.Kakao.isInitialized &&
      window.Kakao.isInitialized()
    )
      return;
    if (!window.Kakao) {
      const script = document.createElement("script");
      script.src = "https://developers.kakao.com/sdk/js/kakao.min.js";
      script.async = true;
      script.onload = () => {
        if (window.Kakao && !window.Kakao.isInitialized()) {
          window.Kakao.init(process.env.REACT_APP_KAKAO_JS_KEY);
        }
      };
      document.body.appendChild(script);
    } else if (!window.Kakao.isInitialized()) {
      window.Kakao.init(process.env.REACT_APP_KAKAO_JS_KEY);
    }
  }, []);

  useEffect(() => {
    if (familyOidHandledRef.current) return;
    familyOidHandledRef.current = true;

    (async () => {
      const params = new URLSearchParams(window.location.search);
      const familyOidParam = params.get("family_oid");
      const familyOidCamel = params.get("familyOid");
      const userName = params.get("userName");
      const familyOid = familyOidParam || familyOidCamel;
      const updateFamilyOid = async (oid: string) => {
        try {
          const res = await request({
            method: "PUT",
            url: "/users/me",
            data: { familyOid: oid },
          });
          console.log("[InvitePage] familyOid update result:", res);
          showAlert("가족 연결이 완료되었습니다.");
        } catch (e) {
          console.error("[InvitePage] familyOid update error:", e);
          showAlert("가족 정보 업데이트에 실패했습니다.");
        }
      };
      if (familyOid) {
        const isConfirm = await confirmDialog({
          text: `가족 연결을 수락하시겠습니까?`,
          title: `${userName} 님의 가족 초대장이 도착했어요`,
          options: {
            trueButtonText: "수락하기",
            falseButtonText: "아니요",
          },
        });
        if (isConfirm) {
          await updateFamilyOid(familyOid);
          const url = new URL(window.location.href);
          params.delete("family_oid");
          params.delete("familyOid");
          url.search = params.toString();
          window.history.replaceState({}, "", url.toString());
        }
      }
    })();
  }, []);

  // family_oid 생성 및 유저 정보 업데이트 함수 (예시)
  // JWT 토큰에서 oid 추출 후 family_oid 생성
  const getOrCreateFamilyOid = async () => {
    let familyOid = familyInfo?.[0]?.familyOid;
    if (!familyOid) {
      // localStorage에서 routine-care-recoil-persist의 user.oid 추출
      let userOid = "";
      try {
        const persist = localStorage.getItem("routine-care-recoil-persist");
        if (persist) {
          const parsed = JSON.parse(persist);
          userOid = parsed.user?.oid || "";
        }
      } catch {
        showAlert("로컬스토리지에서 사용자 정보를 읽을 수 없습니다.");
      }
      if (userOid) {
        familyOid = userOid.replace("users-", "family-");
        // 유저 정보에 familyOid 업데이트 (프로젝트 표준 API)
        try {
          await request({
            method: "PUT",
            url: "/users/me",
            data: { familyOid },
          });
        } catch (e) {
          showAlert("가족 정보 업데이트에 실패했습니다.");
        }
      }
    }
    return familyOid;
  };

  // OS/브라우저 기본 공유 기능(Web Share API)
  // const shareInviteLink = async () => {
  //   const familyOid = await getOrCreateFamilyOid();
  //   const url = `${window.location.origin}/myinfo/invite?familyOid=${familyOid}`;
  //   const title = "가족 초대 링크";
  //   const text = "가족 초대 링크입니다! 함께 건강 리포트를 확인하세요.";

  //   if (navigator.share) {
  //     try {
  //       await navigator.share({ title, text, url });
  //       console.log("[Invite] Web Share로 초대 링크 전달 완료");
  //     } catch (err) {
  //       showAlert("공유가 취소되었거나 실패했습니다.");
  //     }
  //   } else {
  //     showAlert(
  //       "이 브라우저에서는 공유 기능을 지원하지 않습니다. 직접 링크를 복사해 주세요."
  //     );
  //   }
  // };

  const copyLink = async () => {
    const familyOid = await getOrCreateFamilyOid();
    const url = `${
      window.location.origin
    }/myinfo/invite?familyOid=${familyOid}&userName=${encodeURIComponent(
      userInfo?.userName ?? ""
    )}`;
    try {
      await navigator.clipboard.writeText(url);
      console.log("[Invite] 초대 링크 복사 완료:", url);
      showAlert("초대 링크를 복사했어요!");
    } catch {
      console.log("[Invite] 클립보드 복사 실패, 대체 동작 수행");
      prompt("초대 링크를 복사하세요:", url);
    }
  };

  const kakaoShare = async () => {
    const familyOid = await getOrCreateFamilyOid();
    const url = `${
      window.location.origin
    }/myinfo/invite?familyOid=${familyOid}&userName=${encodeURIComponent(
      userInfo?.userName ?? ""
    )}`;
    const title = "가족 초대장이 도착했어요❤️";
    const description = "루틴케어에서 건강을 함께 관리해요 💪";
    const imageUrl = `${window.location.origin}/logo.png`;

    // 1. React Native WebView 브릿지
    if (
      window.ReactNativeWebView &&
      typeof window.ReactNativeWebView.postMessage === "function"
    ) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: "KAKAO_SHARE",
          url,
          title,
          description,
          imageUrl,
        })
      );
      return;
    }

    // 2. iOS WKWebView 브릿지
    if (
      window.webkit &&
      window.webkit.messageHandlers &&
      window.webkit.messageHandlers.kakaoShare
    ) {
      window.webkit.messageHandlers.kakaoShare.postMessage(
        JSON.stringify({ url, title, description, imageUrl })
      );
      return;
    }

    // 3. Fallback: 기존 JS SDK
    if (window.Kakao && window.Kakao.Link) {
      window.Kakao.Link.sendDefault({
        objectType: "feed",
        content: {
          title,
          description,
          imageUrl,
          link: {
            mobileWebUrl: url,
            webUrl: url,
          },
        },
        buttons: [
          {
            title: "초대 수락하기",
            link: {
              mobileWebUrl: url,
              webUrl: url,
            },
          },
        ],
      });
    } else {
      alert("카카오톡 공유 기능을 사용할 수 없습니다.");
    }
  };

  return (
    <PageContainer isNavBar={true} active="myinfo">
      <>
        {/* 헤더 */}
        <SubConHeader
          title="가족 연결하기"
          onClickArrow={() => navi("/myinfo")}
        />

        <main style={{ paddingBottom: "80px" }}>
          {/* 타이틀/설명 */}
          <section style={{ marginTop: px(8) }}>
            <div
              style={{ fontSize: px(18), fontWeight: 800, color: "#111827" }}
            >
              가족을 초대해보세요
            </div>
            <div
              style={{
                fontSize: px(12),
                color: "#6B7280",
                marginTop: px(6),
                lineHeight: 1.5,
              }}
            >
              초대 링크를 통해 가족과 계정을 연결하면,
              <br />
              건강 리포트를 함께 확인하고 루틴을 응원할 수 있어요.
            </div>
          </section>

          {/* 초대 방법 카드 */}
          <section
            style={{
              marginTop: px(8),
              background: "#FFFFFF",
              borderRadius: px(14),
              boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
              padding: px(14),
            }}
          >
            <div
              style={{
                marginBottom: px(12),
                fontSize: px(13),
                fontWeight: 700,
                color: "#111827",
              }}
            >
              초대 방법 선택
            </div>

            <button
              onClick={kakaoShare}
              style={{
                width: "100%",
                height: px(52),
                borderRadius: px(12),
                border: "none",
                background: "#FEE500",
                color: "#111827",
                fontSize: px(14),
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: px(10),
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden>
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12.0006 1C5.98197 1 0.5 5.07604 0.5 10.1043C0.5 13.2307 2.49089 15.9872 5.52322 17.6272L4.24793 22.4028C4.1342 22.8249 4.60573 23.1623 4.96736 22.9174L10.5605 19.1344C11.032 19.1813 11.5125 19.2087 12.0006 19.2087C18.3515 19.2087 23.5 15.1313 23.5 10.1043C23.5 5.07604 18.3515 1 12.0006 1Z"
                  fill="black"
                  fillOpacity="0.902"
                />
              </svg>
              카카오톡으로 초대 링크 바로 전송
            </button>

            <button
              onClick={copyLink}
              style={{
                width: "100%",
                height: px(48),
                marginTop: px(10),
                borderRadius: px(12),
                border: `${px(1)}px solid #E5E7EB`,
                background: "#FFFFFF",
                color: "#111827",
                fontSize: px(14),
                display: "flex",
                alignItems: "center",
                gap: px(10),
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <svg
                width={px(18)}
                height={px(18)}
                viewBox="0 0 24 24"
                aria-hidden
              >
                <rect
                  x="8"
                  y="8"
                  width="10"
                  height="12"
                  rx="2"
                  stroke="#6B7280"
                  strokeWidth="2"
                  fill="none"
                />
                <path
                  d="M6 12V6a2 2 0 0 1 2-2h8"
                  stroke="#6B7280"
                  strokeWidth="2"
                  fill="none"
                />
              </svg>
              초대 링크 복사
            </button>

            {/* 주의사항 */}
            <div
              style={{
                marginTop: px(12),
                fontSize: px(13),
                fontWeight: 700,
                color: "#6B7280",
              }}
            >
              초대 시 주의사항
            </div>
            <ul
              style={{
                marginTop: px(8),
                paddingLeft: px(4),
                listStyle: "none",
                lineHeight: 1.7,
              }}
            >
              {[
                "초대 코드는 24시간 후 만료됩니다",
                "최대 5명까지 연결 가능합니다",
                "가족이 수락하면 자동으로 연결됩니다",
              ].map((t, i) => (
                <li
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: px(8),
                    color: "#6B7280",
                    fontSize: px(12),
                  }}
                >
                  <span
                    style={{
                      width: px(4),
                      height: px(4),
                      marginTop: px(7),
                      borderRadius: "50%",
                      background: "#D1D5DB",
                      flex: "0 0 auto",
                    }}
                  />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* 연결된 가족 미리보기 (헤더/카운트 없이 그리드만) */}
          <section style={{ marginTop: px(12) }}>
            <ConnectedFamilyCard
              members={familyInfo || []}
              count={familyInfo?.length || 0}
              px={px}
              showChevron={false} // 필요 없으면 false
              showAddButton={false} // ← 여기만 숨김
            />
          </section>
        </main>
      </>
    </PageContainer>
  );
}

export default InvitePage;
